import DirListTreeItem from "./DirListTreeItem";
import { DirListTreeSupport } from "./DirListTreeSupport";
import {
  ChevronRightRounded as CollapseIcon,
  ExpandMoreRounded as ExpandIcon,
} from "@mui/icons-material";
import { SimpleTreeView } from "@mui/x-tree-view";
import { isArray } from "common/arrays";
import { useCallback, useState } from "react";
import { connect } from "react-redux";
import * as actions from "store/actions/input/attachments/attachmentsActions";
import * as selectors from "store/selectors/input/attachments/attachmentsSelectors";
import * as tempSelectors from "store/selectors/temp/attachments/tempAttachmentsSelectors";

export const FileListTree = ({
  setAttachmentName,
  setAttachmentDocumentType,
  fileTrees,
  attachments,
  refreshMetadata,
  markAttachmentHidden,
  markDownloadState,
  getDownloadState,
  createFileButtons,
  createDirButtons,
  deleteDir,
  setDirName,
  addDir,
  moveFileToDirectory,
  moveDirectory,
  moveFileToFile,
  processLossRunRef,
  processSicCodesRef,
}) => {
  const embeddedFiles = useCallback(
    (fileId) => tempSelectors.embeddedFilesIn(fileTrees, fileId),
    [fileTrees]
  );
  const [expanded, setExpanded] = useState([""]);
  const [selected, setSelected] = useState([]);
  const expand = (id) => {
    if (!expanded.includes(id)) {
      setExpanded([...expanded, id]);
    }
  };

  const getExpanded = () => expanded;
  const fileSystem = new DirListTreeSupport(
    attachments,
    {
      addDir,
      deleteDir,
      setDirName,
      createFileButtons,
      moveDirectory,
      createDirButtons,
      getExpanded,
      expand,
    },
    {
      initiateRefresh: refreshMetadata,
      markAttachmentHidden,
      setAttachmentName,
      setAttachmentDocumentType,
      markDownloadState,
      getDownloadState,
      noFileEditing: false,
      moveFileToDirectory,
      moveFileToFile,
      embeddedFiles,
      processLossRun: (fileId, filename) => {
        processLossRunRef.current.start(fileId, filename);
      },
      processSicCodes: (fileId, filename) => {
        processSicCodesRef.current.start(fileId, filename);
      },
    },
    fileTrees
  );

  const treeItemIdsToSupports = (itemIds) => {
    const nodeIds = [
      ...(isArray(itemIds) ? itemIds : itemIds != null ? [itemIds] : []),
    ];
    const reduceFunc = (acc, current) => {
      for (const support of [
        ...(current.children ?? []),
        ...(current.files ?? []),
        ...(current.directories ?? []),
      ]) {
        reduceFunc(acc, support);
      }
      if (nodeIds.includes(current.getUniqueId())) {
        acc.push(current);
      }
      return acc;
    };
    return [...fileSystem.files, ...fileSystem.directories].reduce(
      reduceFunc,
      []
    );
  };

  return (
    <SimpleTreeView
      multiSelect={true}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
      className={"file-tree"}
      expandedItems={expanded}
      onExpandedItemsChange={(e, itemIds) => {
        const meta =
          navigator.platform.startsWith("Mac") || navigator.platform === "iPad";
        const targetKey = (meta ? e.metaKey : e.ctrlKey) || e.shiftKey;
        if (!targetKey) {
          setExpanded(itemIds);
        }
      }}
      selectedItems={selected.map((s) => s.getUniqueId())}
      onSelectedItemsChange={(event, itemIds) => {
        setSelected(treeItemIdsToSupports(itemIds));
      }}
      aria-label={"file system navigator"}
      slots={{
        collapseIcon: ExpandIcon,
        expandIcon: CollapseIcon,
      }}
      sx={{ flexGrow: 1, overflowY: "auto" }}
    >
      <DirListTreeItem
        selected={selected}
        setSelected={setSelected}
        dirSupport={fileSystem}
      ></DirListTreeItem>
    </SimpleTreeView>
  );
};

const mapDispatchToProps = {
  setAttachmentName: actions.updateAttachmentName,
  setAttachmentDocumentType: actions.updateAttachmentDocumentType,
  refreshMetadata: actions.metadataRefresh,
  markAttachmentHidden: actions.removeFileFromDirectory,
  deleteDir: actions.removeDirectory,
  setDirName: actions.renameDirectory,
  addDir: actions.createDirectory,
  moveFileToDirectory: actions.moveFileToDirectory,
  moveDirectory: actions.moveDirectory,
  moveFileToFile: actions.moveFileToFile,
};

const mapStateToProps = (state) => ({
  fileTrees: tempSelectors.attachedFileTrees(state),
  attachments: selectors.getAttachments(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(FileListTree);
